/**
 * Manage Orders Screen
 * View and manage customer orders for the shop
 * Full CRUD operations with shop theming
 */

import React, { useState, useEffect } from 'react';
import { View, ScrollView, StyleSheet, TouchableOpacity, Modal, ActivityIndicator, Alert } from 'react-native';
import { useAuthTokens, useCurrentShop, useThemeColors, useThemeAccent, getConfig } from '@app/core';
import { Button, Input, Card, Text } from '@app/design-system';
import { colors } from '@app/design-system';
import { ShopHeader } from '@modules/shop-shell';

interface ManageOrdersScreenProps {
  onBack?: () => void;
  onHome?: () => void;
  onManageShop?: () => void;
  onManageUser?: () => void;
  onManageCatalog?: () => void;
  onWebsiteBuilder?: () => void;
  onLogout?: () => void;
}

interface OrderItem {
  catalogId: string;
  quantity: number;
  unit: string;
  pricePerUnit: number;
  discount?: number;              // Item-level discount percentage
  effectivePricePerUnit?: number; // Price after item discount
  lineTotal?: number;             // Total for this line item
}

interface CatalogDetails {
  id: string;
  name: string;
  description?: string;
  category?: string;
  unit?: string;
  imageUrl?: string;
}

interface DeliveryLocation {
  type: string;
  coordinates: [number, number]; // [longitude, latitude]
  longitude: number;
  latitude: number;
  valid: boolean;
}

interface Order {
  id: string;
  shopId: string;
  customerName?: string;
  mobileCountryCode: string;
  customerMobile: string;
  customerEmail?: string;
  deliveryAddress?: string;
  deliveryLocation?: DeliveryLocation;
  deliveryDistance?: number;
  deliveryCharges?: number;
  discount?: number;          // Discount amount (flat) or percentage
  discountType?: 'FLAT' | 'PERCENTAGE'; // Type of discount
  items: OrderItem[];
  totalAmount: number;
  orderSource: string;
  status: 'CREATED' | 'ACCEPTED' | 'REJECTED' | 'CANCELED' | 'DELIVERED';
  createdAt: string;
  updatedAt: string;
  updatedBy?: string;
}

interface EditOrderForm {
  items: OrderItem[];
  status: string;
  totalAmount: number;
  deliveryCharges?: number;
  discount?: number;
  discountType?: 'FLAT' | 'PERCENTAGE';
}

// Order Status Priority for Sorting
const STATUS_PRIORITY: { [key: string]: number } = {
  CREATED: 1,
  ACCEPTED: 2,
  DELIVERED: 3,
  REJECTED: 4,
  CANCELED: 5,
};

// Order API Service
const orderService = {
  getOrders: async (shopId: string, accessToken: string, filters?: { status?: string; fromDate?: string; toDate?: string }) => {
    const config = getConfig();
    const params = new URLSearchParams();

    if (filters?.status) params.append('status', filters.status);
    if (filters?.fromDate) params.append('fromDate', filters.fromDate);
    if (filters?.toDate) params.append('toDate', filters.toDate);

    const url = params.toString() ? `${config.apiBaseUrl}/api/orders?${params.toString()}` : `${config.apiBaseUrl}/api/orders`;

    console.log('📡 [OrderService] Fetching orders with filters:', filters);

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
      },
    });

    console.log('📡 [OrderService] Response status:', response.status);

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ message: 'Failed to fetch orders' }));
      console.error('❌ [OrderService] Get orders error:', errorData);
      throw new Error(errorData.message || 'Failed to fetch orders');
    }

    return response.json();
  },

  updateOrder: async (orderId: string, accessToken: string, updates: Partial<Order>) => {
    const config = getConfig();
    console.log('📡 [OrderService] Updating order:', orderId);

    const response = await fetch(`${config.apiBaseUrl}/api/orders/${orderId}`, {
      method: 'PATCH',
      headers: {
        'Authorization': `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(updates),
    });

    console.log('📡 [OrderService] Update response:', response.status);

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ message: 'Failed to update order' }));
      console.error('❌ [OrderService] Update order error:', errorData);
      throw new Error(errorData.message || 'Failed to update order');
    }

    return response.json();
  },

  getCatalogById: async (catalogId: string, accessToken: string): Promise<CatalogDetails> => {
    const config = getConfig();
    console.log('📡 [OrderService] Fetching catalog:', catalogId);

    const response = await fetch(`${config.apiBaseUrl}/api/catalogs/${catalogId}`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
      },
    });

    console.log('📡 [OrderService] Catalog response:', response.status);

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ message: 'Failed to fetch catalog' }));
      console.error('❌ [OrderService] Get catalog error:', errorData);
      throw new Error(errorData.message || 'Failed to fetch catalog');
    }

    return response.json();
  },
};

// Lazy loading component for order item with catalog details - moved outside main component
const OrderItemWithDetails: React.FC<{
  item: OrderItem;
  index: number;
  catalogCache: Map<string, CatalogDetails>;
  setCatalogCache: React.Dispatch<React.SetStateAction<Map<string, CatalogDetails>>>;
  accentColor: string;
  tokens: any;
}> = ({ item, index, catalogCache, setCatalogCache, accentColor, tokens }) => {
    const [catalogDetails, setCatalogDetails] = useState<CatalogDetails | null>(null);
    const [loadingCatalog, setLoadingCatalog] = useState(true);

    useEffect(() => {
      const fetchCatalogDetails = async () => {
        // Check cache first
        if (catalogCache.has(item.catalogId)) {
          setCatalogDetails(catalogCache.get(item.catalogId)!);
          setLoadingCatalog(false);
          return;
        }

        try {
          if (!tokens?.oauthToken) return;

          const details = await orderService.getCatalogById(item.catalogId, tokens.oauthToken);
          setCatalogDetails(details);

          // Update cache
          setCatalogCache(prev => new Map(prev).set(item.catalogId, details));
          setLoadingCatalog(false);
        } catch (error) {
          console.error('Failed to fetch catalog details:', error);
          setLoadingCatalog(false);
        }
      };

      fetchCatalogDetails();
    }, [item.catalogId, catalogCache, setCatalogCache, tokens]);

    if (loadingCatalog) {
      return (
        <View style={styles.itemRow}>
          <ActivityIndicator size="small" color={accentColor} />
          <Text variant="caption" color="secondary" style={styles.loadingItemText}>
            Loading item details...
          </Text>
        </View>
      );
    }

    const hasItemDiscount = (item.discount || 0) > 0;
    const effectivePrice = item.effectivePricePerUnit || item.pricePerUnit;
    const lineTotal = item.lineTotal || (item.quantity * effectivePrice);

    return (
      <View style={styles.itemDetailRow}>
        <View style={styles.itemInfo}>
          <View style={styles.itemNamePriceRow}>
            <Text variant="body" bold style={styles.itemName}>
              {catalogDetails?.name || `Item #${item.catalogId.slice(-6)}`}
              {catalogDetails?.category && (
                <Text variant="caption" color="secondary" style={styles.itemCategoryInline}>
                  {' '}({catalogDetails.category})
                </Text>
              )}
              {hasItemDiscount && (
                <Text variant="caption" style={{ color: '#34C759', fontWeight: 'bold', marginLeft: 4 }}>
                  {' '}🏷️ {item.discount}% OFF
                </Text>
              )}
            </Text>
            <View style={styles.itemPriceInfo}>
              {hasItemDiscount ? (
                <>
                  <Text variant="caption" color="secondary" style={[styles.itemQuantityText, { textDecorationLine: 'line-through' }]}>
                    {item.quantity} {item.unit} × ₹{item.pricePerUnit}
                  </Text>
                  <Text variant="caption" style={{ color: '#34C759', fontWeight: 'bold' }}>
                    {item.quantity} {item.unit} × ₹{effectivePrice.toFixed(2)}
                  </Text>
                </>
              ) : (
                <Text variant="caption" color="secondary" style={styles.itemQuantityText}>
                  {item.quantity} {item.unit} × ₹{item.pricePerUnit}
                </Text>
              )}
              <Text variant="body" bold style={[styles.itemTotalPrice, { color: accentColor }]}>
                ₹{lineTotal.toFixed(2)}
              </Text>
            </View>
          </View>
          {catalogDetails?.description && (
            <Text variant="caption" color="secondary" style={styles.itemDescription}>
              {catalogDetails.description}
            </Text>
          )}
        </View>
      </View>
    );
  };

// Lazy loading component for edit modal item with catalog details - moved outside main component
const EditOrderItemWithDetails: React.FC<{
  item: OrderItem;
  index: number;
  catalogCache: Map<string, CatalogDetails>;
  setCatalogCache: React.Dispatch<React.SetStateAction<Map<string, CatalogDetails>>>;
  accentColor: string;
  tokens: any;
  handleUpdateItem: (index: number, field: 'quantity' | 'pricePerUnit', value: string) => void;
}> = ({ item, index, catalogCache, setCatalogCache, accentColor, tokens, handleUpdateItem }) => {
    const [catalogDetails, setCatalogDetails] = useState<CatalogDetails | null>(null);
    const [loadingCatalog, setLoadingCatalog] = useState(true);

    useEffect(() => {
      const fetchCatalogDetails = async () => {
        // Check cache first
        if (catalogCache.has(item.catalogId)) {
          setCatalogDetails(catalogCache.get(item.catalogId)!);
          setLoadingCatalog(false);
          return;
        }

        try {
          if (!tokens?.oauthToken) return;

          const details = await orderService.getCatalogById(item.catalogId, tokens.oauthToken);
          setCatalogDetails(details);

          // Update cache
          setCatalogCache(prev => new Map(prev).set(item.catalogId, details));
          setLoadingCatalog(false);
        } catch (error) {
          console.error('Failed to fetch catalog details:', error);
          setLoadingCatalog(false);
        }
      };

      fetchCatalogDetails();
    }, [item.catalogId, catalogCache, setCatalogCache, tokens]);

    return (
      <View style={styles.itemEditRow}>
        {/* Catalog Item Details Header with inline pricing */}
        <View style={styles.editItemHeader}>
          {loadingCatalog ? (
            <View style={styles.editItemLoadingRow}>
              <ActivityIndicator size="small" color={accentColor} />
              <Text variant="caption" color="secondary" style={styles.loadingItemText}>
                Loading item details...
              </Text>
            </View>
          ) : (
            <>
              <View style={styles.editItemNamePriceRow}>
                <Text variant="body" bold style={styles.editItemName}>
                  {catalogDetails?.name || `Item #${item.catalogId.slice(-6)}`}
                  {catalogDetails?.category && (
                    <Text variant="caption" color="secondary" style={styles.editItemCategoryInline}>
                      {' '}({catalogDetails.category})
                    </Text>
                  )}
                  {(item.discount || 0) > 0 && (
                    <Text variant="caption" style={{ color: '#34C759', fontWeight: 'bold', marginLeft: 4 }}>
                      {' '}🏷️ {item.discount}% OFF
                    </Text>
                  )}
                </Text>
                <View style={{ alignItems: 'flex-end' }}>
                  {(item.discount || 0) > 0 ? (
                    <>
                      <Text variant="caption" color="secondary" style={{ textDecorationLine: 'line-through' }}>
                        ₹{(item.quantity * item.pricePerUnit).toFixed(2)}
                      </Text>
                      <Text variant="body" bold style={[styles.editItemTotal, { color: '#34C759' }]}>
                        ₹{(item.lineTotal || (item.quantity * (item.effectivePricePerUnit || item.pricePerUnit))).toFixed(2)}
                      </Text>
                    </>
                  ) : (
                    <Text variant="body" bold style={[styles.editItemTotal, { color: accentColor }]}>
                      ₹{(item.quantity * item.pricePerUnit).toFixed(2)}
                    </Text>
                  )}
                </View>
              </View>
              {catalogDetails?.description && (
                <Text variant="caption" color="secondary" style={styles.editItemDescription}>
                  {catalogDetails.description}
                </Text>
              )}
            </>
          )}
        </View>

        {/* Edit Inputs - Compact Row */}
        <View style={styles.itemInputsCompact}>
          <View style={styles.itemInputFieldCompact}>
            <Text variant="caption" style={styles.inputLabelCompact}>Qty ({item.unit})</Text>
            <Input
              value={item.quantity.toString()}
              onChangeText={(value) => handleUpdateItem(index, 'quantity', value)}
              keyboardType="numeric"
              style={styles.itemInputCompact}
            />
          </View>
          <View style={styles.itemInputFieldCompact}>
            <Text variant="caption" style={styles.inputLabelCompact}>
              ₹/Unit{(item.discount || 0) > 0 ? ` (after ${item.discount}% off)` : ''}
            </Text>
            <Input
              value={item.pricePerUnit.toString()}
              onChangeText={(value) => handleUpdateItem(index, 'pricePerUnit', value)}
              keyboardType="numeric"
              style={styles.itemInputCompact}
            />
          </View>
        </View>
      </View>
    );
  };

export const ManageOrdersScreen: React.FC<ManageOrdersScreenProps> = ({
  onBack,
  onHome,
  onManageShop,
  onManageUser,
  onManageCatalog,
  onWebsiteBuilder,
  onLogout,
}) => {
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingOrder, setEditingOrder] = useState<Order | null>(null);
  const [saving, setSaving] = useState(false);
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [fromDate, setFromDate] = useState<string>('');
  const [toDate, setToDate] = useState<string>('');
  const [showAdvancedFilters, setShowAdvancedFilters] = useState<boolean>(false);
  const [catalogCache, setCatalogCache] = useState<Map<string, CatalogDetails>>(new Map());
  const [editForm, setEditForm] = useState<EditOrderForm>({
    items: [],
    status: 'CREATED',
    totalAmount: 0,
  });

  const tokens = useAuthTokens();
  const shop = useCurrentShop();
  const themeColors = useThemeColors();
  const accentColor = useThemeAccent();

  useEffect(() => {
    if (tokens?.shopId && tokens?.oauthToken) {
      loadOrders();
    }
  }, [tokens?.shopId, tokens?.oauthToken]);

  const loadOrders = async () => {
    try {
      setLoading(true);
      if (!tokens?.shopId || !tokens?.oauthToken) {
        throw new Error('Shop ID or access token not found');
      }

      const filters: any = {};
      if (statusFilter) filters.status = statusFilter;
      if (fromDate) filters.fromDate = fromDate;
      if (toDate) filters.toDate = toDate;

      const ordersData = await orderService.getOrders(tokens.shopId, tokens.oauthToken, filters);

      // Sort orders by status priority, then by date
      const sortedOrders = ordersData.sort((a: Order, b: Order) => {
        const priorityDiff = (STATUS_PRIORITY[a.status] || 999) - (STATUS_PRIORITY[b.status] || 999);
        if (priorityDiff !== 0) return priorityDiff;
        return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
      });

      setOrders(sortedOrders);
    } catch (error: any) {
      console.error('❌ [ManageOrders] Load orders error:', error);
      Alert.alert('Error', error.message || 'Failed to load orders');
    } finally {
      setLoading(false);
    }
  };

  const handleEditOrder = (order: Order) => {
    setEditingOrder(order);

    // Calculate items subtotal considering item-level discounts
    const itemsSubtotal = order.items.reduce((sum, item) => {
      const effectivePrice = item.effectivePricePerUnit || item.pricePerUnit;
      const lineTotal = item.lineTotal || (item.quantity * effectivePrice);
      return sum + lineTotal;
    }, 0);

    // Calculate order-level discount amount if present
    const orderDiscount = order.discount || 0;
    const discountType = order.discountType || 'FLAT';
    const orderDiscountAmt = computeDiscountAmount(itemsSubtotal, orderDiscount, discountType);

    // Calculate total: itemsSubtotal + delivery - orderDiscount
    const calculatedTotal = itemsSubtotal + (order.deliveryCharges || 0) - orderDiscountAmt;

    setEditForm({
      items: [...order.items],
      status: order.status,
      totalAmount: Math.max(0, calculatedTotal),
      deliveryCharges: order.deliveryCharges || 0,
      discount: orderDiscount,
      discountType: discountType,
    });
    setShowEditModal(true);
  };

  // Helper: compute discount amount from subtotal
  const computeDiscountAmount = (subtotal: number, discount: number, discountType: 'FLAT' | 'PERCENTAGE'): number => {
    if (!discount || discount <= 0) return 0;
    if (discountType === 'PERCENTAGE') {
      return Math.min((subtotal * discount) / 100, subtotal);
    }
    return Math.min(discount, subtotal);
  };

  const handleUpdateItem = (index: number, field: 'quantity' | 'pricePerUnit', value: string) => {
    const updatedItems = [...editForm.items];
    const item = updatedItems[index];

    // Update the field
    updatedItems[index] = {
      ...item,
      [field]: parseFloat(value) || 0,
    };

    // Recalculate effectivePricePerUnit and lineTotal based on item discount
    const newItem = updatedItems[index];
    const itemDiscount = newItem.discount || 0;

    if (itemDiscount > 0) {
      // Apply item-level discount
      newItem.effectivePricePerUnit = newItem.pricePerUnit * (1 - itemDiscount / 100);
      newItem.lineTotal = newItem.quantity * newItem.effectivePricePerUnit;
    } else {
      newItem.effectivePricePerUnit = newItem.pricePerUnit;
      newItem.lineTotal = newItem.quantity * newItem.pricePerUnit;
    }

    // Recalculate total amount considering item-level discounts
    const itemsTotal = updatedItems.reduce((sum, item) => {
      return sum + (item.lineTotal || (item.quantity * (item.effectivePricePerUnit || item.pricePerUnit)));
    }, 0);

    const discountAmt = computeDiscountAmount(itemsTotal, editForm.discount || 0, editForm.discountType || 'FLAT');
    const newTotal = itemsTotal + (editForm.deliveryCharges || 0) - discountAmt;

    setEditForm({
      ...editForm,
      items: updatedItems,
      totalAmount: Math.max(0, newTotal),
    });
  };

  const handleUpdateDeliveryCharges = (value: string) => {
    const deliveryCharges = Math.max(0, parseFloat(value) || 0);

    // Recalculate total amount considering item-level discounts
    const itemsTotal = editForm.items.reduce((sum, item) => {
      const effectivePrice = item.effectivePricePerUnit || item.pricePerUnit;
      const lineTotal = item.lineTotal || (item.quantity * effectivePrice);
      return sum + lineTotal;
    }, 0);

    const discountAmt = computeDiscountAmount(itemsTotal, editForm.discount || 0, editForm.discountType || 'FLAT');
    const newTotal = itemsTotal + deliveryCharges - discountAmt;

    setEditForm({
      ...editForm,
      deliveryCharges,
      totalAmount: Math.max(0, newTotal),
    });
  };

  const handleUpdateDiscount = (value: string) => {
    const discount = Math.max(0, parseFloat(value) || 0);
    const discountType = editForm.discountType || 'FLAT';

    const itemsTotal = editForm.items.reduce((sum, item) => {
      const effectivePrice = item.effectivePricePerUnit || item.pricePerUnit;
      const lineTotal = item.lineTotal || (item.quantity * effectivePrice);
      return sum + lineTotal;
    }, 0);

    const discountAmt = computeDiscountAmount(itemsTotal, discount, discountType);
    const newTotal = itemsTotal + (editForm.deliveryCharges || 0) - discountAmt;

    setEditForm({
      ...editForm,
      discount,
      totalAmount: Math.max(0, newTotal),
    });
  };

  const handleUpdateDiscountType = (discountType: 'FLAT' | 'PERCENTAGE') => {
    const itemsTotal = editForm.items.reduce((sum, item) => {
      const effectivePrice = item.effectivePricePerUnit || item.pricePerUnit;
      const lineTotal = item.lineTotal || (item.quantity * effectivePrice);
      return sum + lineTotal;
    }, 0);

    const discountAmt = computeDiscountAmount(itemsTotal, editForm.discount || 0, discountType);
    const newTotal = itemsTotal + (editForm.deliveryCharges || 0) - discountAmt;

    setEditForm({
      ...editForm,
      discountType,
      totalAmount: Math.max(0, newTotal),
    });
  };

  const handleSaveOrder = async () => {
    try {
      if (!editingOrder || !tokens?.oauthToken) return;

      setSaving(true);

      console.log('📋 [ManageOrders] ====== EDIT FORM DATA ======');
      console.log('📋 [ManageOrders] Edit Form Items:', JSON.stringify(editForm.items, null, 2));
      console.log('📋 [ManageOrders] Delivery Charges:', editForm.deliveryCharges);
      console.log('📋 [ManageOrders] Order Discount:', editForm.discount);
      console.log('📋 [ManageOrders] Discount Type:', editForm.discountType);
      console.log('📋 [ManageOrders] Total Amount:', editForm.totalAmount);

      // Prepare items with item-level discounts already applied in lineTotal
      const itemsToSend = editForm.items.map(item => ({
        catalogId: item.catalogId,
        quantity: item.quantity,
        unit: item.unit,
        pricePerUnit: item.pricePerUnit,
        discount: item.discount || 0,
        effectivePricePerUnit: item.effectivePricePerUnit || item.pricePerUnit,
        lineTotal: item.lineTotal || (item.quantity * (item.effectivePricePerUnit || item.pricePerUnit))
      }));

      const updates: any = {
        items: itemsToSend,
        status: editForm.status,
        totalAmount: editForm.totalAmount,
      };

      // Include deliveryCharges (can be 0 or positive)
      updates.deliveryCharges = editForm.deliveryCharges || 0;

      // Order-level discount: only include if present
      if (editForm.discount && editForm.discount > 0) {
        updates.discount = editForm.discount;
        updates.discountType = editForm.discountType || 'FLAT';
      } else {
        // Explicitly clear order-level discount (item discounts are in lineTotal)
        updates.discount = 0;
        updates.discountType = null;
      }

      console.log('📤 [ManageOrders] ====== SENDING TO API ======');
      console.log('📤 [ManageOrders] Order ID:', editingOrder.id);
      console.log('📤 [ManageOrders] Update Payload:', JSON.stringify(updates, null, 2));

      await orderService.updateOrder(editingOrder.id, tokens.oauthToken, updates);

      Alert.alert('Success', 'Order updated successfully');
      setShowEditModal(false);
      setEditingOrder(null);
      loadOrders();
    } catch (error: any) {
      console.error('❌ [ManageOrders] Save order error:', error);
      Alert.alert('Error', error.message || 'Failed to update order');
    } finally {
      setSaving(false);
    }
  };

  const handleQuickStatusUpdate = async (order: Order, newStatus: 'ACCEPTED' | 'DELIVERED' | 'REJECTED') => {
    try {
      if (!tokens?.oauthToken) return;

      const statusLabels = {
        ACCEPTED: 'Accept',
        DELIVERED: 'Mark as Delivered',
        REJECTED: 'Reject'
      };

      if (typeof window !== 'undefined') {
        const confirmed = window.confirm(`Are you sure you want to ${statusLabels[newStatus]} this order?`);
        if (!confirmed) return;
      }

      console.log(`🔄 [ManageOrders] Updating order ${order.id} to ${newStatus}`);
      await orderService.updateOrder(order.id, tokens.oauthToken, { status: newStatus });

      Alert.alert('Success', `Order ${statusLabels[newStatus].toLowerCase()}ed successfully`);
      loadOrders();
    } catch (error: any) {
      console.error('❌ [ManageOrders] Quick status update error:', error);
      Alert.alert('Error', error.message || 'Failed to update order status');
    }
  };

  const handleOpenMap = (order: Order) => {
    try {
      if (!order.deliveryLocation || !order.deliveryLocation.valid) {
        Alert.alert('No Location', 'Delivery location is not available for this order');
        return;
      }

      // Use latitude and longitude fields directly, fallback to coordinates array
      const latitude = order.deliveryLocation.latitude || order.deliveryLocation.coordinates[1];
      const longitude = order.deliveryLocation.longitude || order.deliveryLocation.coordinates[0];

      // Skip if coordinates are invalid (0, 0)
      if (latitude === 0 && longitude === 0) {
        Alert.alert('Invalid Location', 'Delivery location coordinates are not set');
        return;
      }

      const label = order.customerName || `Order #${order.id.slice(-8)}`;

      // For web, open Google Maps in a new tab
      if (typeof window !== 'undefined') {
        const googleMapsUrl = `https://maps.google.com/?q=${latitude},${longitude}`;
        window.open(googleMapsUrl, '_blank');
      } else {
        // For native apps, use platform-specific map URLs
        const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);

        if (isIOS) {
          // Apple Maps URL scheme for iOS
          const appleMapsUrl = `maps://maps.apple.com/?q=${label}&ll=${latitude},${longitude}`;
          window.location.href = appleMapsUrl;
        } else {
          // Google Maps URL scheme for Android
          const googleMapsUrl = `geo:${latitude},${longitude}?q=${label}`;
          window.location.href = googleMapsUrl;
        }
      }
    } catch (error) {
      console.error('❌ [ManageOrders] Open map error:', error);
      Alert.alert('Error', 'Failed to open map');
    }
  };

  const handlePrintInvoice = async (order: Order) => {
    try {
      console.log('🖨️ [ManageOrders] Printing invoice for order:', order.id);

      // Generate invoice HTML content
      const invoiceHTML = await generateInvoiceHTML(order);

      // Create a new window for printing
      const printWindow = window.open('', '_blank');
      if (printWindow) {
        printWindow.document.write(invoiceHTML);
        printWindow.document.close();

        // Wait for content to load, then print
        printWindow.onload = () => {
          printWindow.print();
        };
      }
    } catch (error: any) {
      console.error('❌ [ManageOrders] Print invoice error:', error);
      Alert.alert('Error', 'Failed to print invoice');
    }
  };

  const handleShareInvoice = async (order: Order) => {
    try {
      console.log('📤 [ManageOrders] Sharing invoice for order:', order.id);

      // Generate invoice HTML content
      const invoiceHTML = await generateInvoiceHTML(order);

      // For web, we'll use the Web Share API if available
      if (navigator.share) {
        // Convert HTML to text for sharing
        const itemsTotal = order.items.reduce((sum, item) => {
          const effectivePrice = item.effectivePricePerUnit || item.pricePerUnit;
          const lineTotal = item.lineTotal || (item.quantity * effectivePrice);
          return sum + lineTotal;
        }, 0);

        // Calculate item-level discount total
        const originalTotal = order.items.reduce((sum, item) => sum + (item.quantity * item.pricePerUnit), 0);
        const itemDiscountTotal = originalTotal - itemsTotal;

        // Calculate order-level discount
        const orderDiscount = order.discount || 0;
        const discountType = order.discountType || 'FLAT';
        const orderDiscountAmt = orderDiscount > 0
          ? (discountType === 'PERCENTAGE' ? Math.min((itemsTotal * orderDiscount) / 100, itemsTotal) : Math.min(orderDiscount, itemsTotal))
          : 0;

        const invoiceText = `
Order Invoice - ${shop?.name || 'My Shop'}
Order #${order.id.slice(-8)}
Date: ${formatDate(order.createdAt)}
Status: ${order.status}

Customer: +${order.mobileCountryCode} ${order.customerMobile}
${order.customerEmail ? `Email: ${order.customerEmail}` : ''}
${order.deliveryAddress ? `Address: ${order.deliveryAddress}` : ''}

Items:
${order.items.map((item, i) => {
  const hasDiscount = (item.discount || 0) > 0;
  const effectivePrice = item.effectivePricePerUnit || item.pricePerUnit;
  const lineTotal = item.lineTotal || (item.quantity * effectivePrice);
  return `${i + 1}. ${item.quantity} ${item.unit} × ₹${effectivePrice.toFixed(2)}${hasDiscount ? ` (${item.discount}% off ₹${item.pricePerUnit})` : ''} = ₹${lineTotal.toFixed(2)}`;
}).join('\n')}

Items Subtotal: ₹${itemsTotal.toFixed(2)}
${itemDiscountTotal > 0 ? `Item Discounts: -₹${itemDiscountTotal.toFixed(2)}` : ''}
${(order.deliveryCharges || 0) > 0 ? `Delivery Charges: ₹${(order.deliveryCharges || 0).toFixed(2)}` : ''}
${orderDiscountAmt > 0 ? `Order Discount: -₹${orderDiscountAmt.toFixed(2)}${discountType === 'PERCENTAGE' ? ` (${orderDiscount}%)` : ''}` : ''}
Total Amount: ₹${order.totalAmount.toFixed(2)}
Source: ${order.orderSource}
        `.trim();

        await navigator.share({
          title: `Invoice - Order #${order.id.slice(-8)}`,
          text: invoiceText,
        });
      } else {
        // Fallback: Open print dialog
        Alert.alert('Share', 'Share functionality requires a mobile device. Use Print Invoice to print instead.');
      }
    } catch (error: any) {
      console.error('❌ [ManageOrders] Share invoice error:', error);
      // User cancelled share or error occurred
      if (error.name !== 'AbortError') {
        Alert.alert('Error', 'Failed to share invoice');
      }
    }
  };

  const generateInvoiceHTML = async (order: Order): Promise<string> => {
    // Fetch catalog details for all items
    const itemsWithDetails = await Promise.all(
      order.items.map(async (item) => {
        try {
          let catalogDetails = catalogCache.get(item.catalogId);
          if (!catalogDetails && tokens?.oauthToken) {
            catalogDetails = await orderService.getCatalogById(item.catalogId, tokens.oauthToken);
            setCatalogCache(prev => new Map(prev).set(item.catalogId, catalogDetails!));
          }
          return { ...item, catalogDetails };
        } catch {
          return { ...item, catalogDetails: null };
        }
      })
    );

    return `
<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <title>Invoice - Order #${order.id.slice(-8)}</title>
  <style>
    body {
      font-family: Arial, sans-serif;
      max-width: 800px;
      margin: 20px auto;
      padding: 20px;
      color: #333;
    }
    .header {
      text-align: center;
      border-bottom: 2px solid #333;
      padding-bottom: 20px;
      margin-bottom: 20px;
    }
    .shop-name {
      font-size: 24px;
      font-weight: bold;
      margin-bottom: 5px;
    }
    .invoice-title {
      font-size: 20px;
      color: #666;
    }
    .section {
      margin: 20px 0;
    }
    .section-title {
      font-size: 16px;
      font-weight: bold;
      margin-bottom: 10px;
      border-bottom: 1px solid #ddd;
      padding-bottom: 5px;
    }
    .info-row {
      display: flex;
      justify-content: space-between;
      padding: 5px 0;
    }
    .info-label {
      font-weight: bold;
      width: 150px;
    }
    table {
      width: 100%;
      border-collapse: collapse;
      margin: 10px 0;
    }
    th, td {
      padding: 10px;
      text-align: left;
      border-bottom: 1px solid #ddd;
    }
    th {
      background-color: #f5f5f5;
      font-weight: bold;
    }
    .item-name {
      font-weight: bold;
    }
    .item-description {
      font-size: 12px;
      color: #666;
    }
    .item-category {
      font-size: 11px;
      color: #999;
    }
    .total-section {
      margin-top: 20px;
      border-top: 2px solid #333;
      padding-top: 10px;
    }
    .total-row {
      display: flex;
      justify-content: space-between;
      font-size: 18px;
      font-weight: bold;
      padding: 10px 0;
    }
    .footer {
      margin-top: 40px;
      text-align: center;
      color: #666;
      font-size: 12px;
      border-top: 1px solid #ddd;
      padding-top: 20px;
    }
    .status-badge {
      display: inline-block;
      padding: 5px 15px;
      border-radius: 15px;
      color: white;
      font-size: 14px;
      font-weight: bold;
    }
    .status-created { background-color: #007AFF; }
    .status-accepted { background-color: #34C759; }
    .status-delivered { background-color: #5856D6; }
    .status-rejected { background-color: #FF3B30; }
    .status-canceled { background-color: #FF9500; }
    @media print {
      body { margin: 0; padding: 10mm; }
    }
  </style>
</head>
<body>
  <div class="header">
    <div class="shop-name">${shop?.name || 'My Shop'}</div>
    <div class="invoice-title">ORDER INVOICE</div>
  </div>

  <div class="section">
    <div class="info-row">
      <div><span class="info-label">Order ID:</span> #${order.id.slice(-8)}</div>
      <div><span class="info-label">Date:</span> ${formatDate(order.createdAt)}</div>
    </div>
    <div class="info-row">
      <div><span class="info-label">Status:</span> <span class="status-badge status-${order.status.toLowerCase()}">${order.status}</span></div>
      <div><span class="info-label">Source:</span> ${order.orderSource}</div>
    </div>
  </div>

  <div class="section">
    <div class="section-title">Customer Details</div>
    <div class="info-row">
      <div><span class="info-label">Mobile:</span> +${order.mobileCountryCode} ${order.customerMobile}</div>
    </div>
    ${order.customerEmail ? `<div class="info-row"><div><span class="info-label">Email:</span> ${order.customerEmail}</div></div>` : ''}
    ${order.deliveryAddress ? `<div class="info-row"><div><span class="info-label">Delivery Address:</span> ${order.deliveryAddress}</div></div>` : ''}
    ${order.deliveryLocation && order.deliveryLocation.valid && (order.deliveryLocation.latitude !== 0 || order.deliveryLocation.longitude !== 0) ? `<div class="info-row"><div><span class="info-label">Location Coordinates:</span> ${order.deliveryLocation.latitude}, ${order.deliveryLocation.longitude}</div></div>` : ''}
  </div>

  <div class="section">
    <div class="section-title">Order Items</div>
    <table>
      <thead>
        <tr>
          <th>#</th>
          <th>Item Details</th>
          <th>Quantity</th>
          <th>Price/Unit</th>
          <th>Discount</th>
          <th>Amount</th>
        </tr>
      </thead>
      <tbody>
        ${itemsWithDetails.map((item, index) => {
          const hasDiscount = (item.discount || 0) > 0;
          const effectivePrice = item.effectivePricePerUnit || item.pricePerUnit;
          const lineTotal = item.lineTotal || (item.quantity * effectivePrice);
          return `
          <tr>
            <td>${index + 1}</td>
            <td>
              <div class="item-name">${item.catalogDetails?.name || `Item #${item.catalogId.slice(-6)}`}</div>
              ${item.catalogDetails?.description ? `<div class="item-description">${item.catalogDetails.description}</div>` : ''}
              ${item.catalogDetails?.category ? `<div class="item-category">📂 ${item.catalogDetails.category}</div>` : ''}
            </td>
            <td>${item.quantity} ${item.unit}</td>
            <td>${hasDiscount ? `<span style="text-decoration: line-through; color: #999;">₹${item.pricePerUnit.toFixed(2)}</span><br><span style="color: #34C759; font-weight: bold;">₹${effectivePrice.toFixed(2)}</span>` : `₹${item.pricePerUnit.toFixed(2)}`}</td>
            <td>${hasDiscount ? `<span style="color: #34C759; font-weight: bold;">${item.discount}%</span>` : '-'}</td>
            <td>₹${lineTotal.toFixed(2)}</td>
          </tr>
        `;
        }).join('')}
      </tbody>
    </table>
  </div>

  <div class="total-section">
    ${(() => {
      const itemsTotal = order.items.reduce((sum, item) => {
        const effectivePrice = item.effectivePricePerUnit || item.pricePerUnit;
        const lineTotal = item.lineTotal || (item.quantity * effectivePrice);
        return sum + lineTotal;
      }, 0);

      // Calculate item-level discount total
      const originalTotal = order.items.reduce((sum, item) => sum + (item.quantity * item.pricePerUnit), 0);
      const itemDiscountTotal = originalTotal - itemsTotal;

      const deliveryCharges = order.deliveryCharges || 0;
      const orderDiscount = order.discount || 0;
      const discountType = order.discountType || 'FLAT';
      const orderDiscountAmt = orderDiscount > 0
        ? (discountType === 'PERCENTAGE' ? Math.min((itemsTotal * orderDiscount) / 100, itemsTotal) : Math.min(orderDiscount, itemsTotal))
        : 0;
      return `
        <div class="total-row" style="font-size: 0.95em; font-weight: normal;">
          <span>Items Subtotal:</span>
          <span>₹${itemsTotal.toFixed(2)}</span>
        </div>
        ${itemDiscountTotal > 0 ? `
        <div class="total-row" style="font-size: 0.95em; font-weight: normal; color: #34C759;">
          <span>Item Discounts:</span>
          <span>- ₹${itemDiscountTotal.toFixed(2)}</span>
        </div>
        ` : ''}
        ${deliveryCharges > 0 ? `
        <div class="total-row" style="font-size: 0.95em; font-weight: normal;">
          <span>Delivery Charges:</span>
          <span>₹${deliveryCharges.toFixed(2)}</span>
        </div>
        ` : ''}
        ${orderDiscountAmt > 0 ? `
        <div class="total-row" style="font-size: 0.95em; font-weight: normal; color: #34C759;">
          <span>Order Discount${discountType === 'PERCENTAGE' ? ` (${orderDiscount}%)` : ''}:</span>
          <span>- ₹${orderDiscountAmt.toFixed(2)}</span>
        </div>
        ` : ''}
        <div class="total-row" style="font-weight: bold; font-size: 1.1em; border-top: 2px solid #333; padding-top: 10px; margin-top: 10px;">
          <span>Final Amount:</span>
          <span>₹${order.totalAmount.toFixed(2)}</span>
        </div>
      `;
    })()}
  </div>

  <div class="footer">
    Thank you for your order!<br>
    Generated on ${new Date().toLocaleString('en-IN')}
  </div>
</body>
</html>
    `.trim();
  };

  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-IN', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateString;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'CREATED': return '#007AFF';
      case 'ACCEPTED': return '#34C759';
      case 'DELIVERED': return '#5856D6';
      case 'REJECTED': return '#FF3B30';
      case 'CANCELED': return '#FF9500';
      default: return colors.gray6;
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'CREATED': return '🆕';
      case 'ACCEPTED': return '✅';
      case 'DELIVERED': return '📦';
      case 'REJECTED': return '❌';
      case 'CANCELED': return '🚫';
      default: return '📋';
    }
  };

  const applyFilters = () => {
    loadOrders();
  };

  const clearFilters = () => {
    setStatusFilter('');
    setFromDate('');
    setToDate('');
    setTimeout(() => loadOrders(), 100);
  };

  if (loading) {
    return (
      <View style={[styles.loadingContainer, { backgroundColor: themeColors.secondary }]}>
        <ActivityIndicator size="large" color={accentColor} />
        <Text variant="body" color="secondary" style={styles.loadingText}>
          Loading orders...
        </Text>
      </View>
    );
  }

  return (
    <>
      {/* Shop Header with Navigation Mode */}
      <ShopHeader
        mode="navigation"
        title="Manage Orders"
        onHome={onHome || onBack}
        onManageShop={onManageShop}
        onManageUser={onManageUser}
        onManageCatalog={onManageCatalog}
        onWebsiteBuilder={onWebsiteBuilder}
        onLogout={onLogout}
      />

      <ScrollView
        contentContainerStyle={[styles.container, { backgroundColor: themeColors.secondary }]}
        keyboardShouldPersistTaps="handled"
      >
        {/* Advanced Filters Card - Collapsible */}
        <Card style={styles.filtersCard}>
          <TouchableOpacity
            onPress={() => setShowAdvancedFilters(!showAdvancedFilters)}
            style={styles.filterHeader}
          >
            <Text variant="h3" bold style={styles.sectionTitle}>
              🔍 Advanced Filters
            </Text>
            <Text style={styles.toggleIcon}>
              {showAdvancedFilters ? '▼' : '▶'}
            </Text>
          </TouchableOpacity>

          {showAdvancedFilters && (
            <>
              {/* Status Filter */}
              <View style={styles.filterRow}>
                <Text variant="body" style={styles.filterLabel}>Status:</Text>
                <View style={styles.statusButtons}>
                  <TouchableOpacity
                    style={[styles.statusButton, statusFilter === '' && { backgroundColor: accentColor }]}
                    onPress={() => setStatusFilter('')}
                  >
                    <Text style={[styles.statusButtonText, statusFilter === '' && styles.statusButtonTextActive]}>All</Text>
                  </TouchableOpacity>
                  {['CREATED', 'ACCEPTED', 'DELIVERED', 'REJECTED', 'CANCELED'].map((status) => (
                    <TouchableOpacity
                      key={status}
                      style={[styles.statusButton, statusFilter === status && { backgroundColor: accentColor }]}
                      onPress={() => setStatusFilter(status)}
                    >
                      <Text style={[styles.statusButtonText, statusFilter === status && styles.statusButtonTextActive]}>
                        {status}
                      </Text>
                    </TouchableOpacity>
                  ))}
                </View>
              </View>

              {/* Date Range Filter with Calendar Inputs */}
              <View style={styles.dateFilterRow}>
                <View style={styles.dateField}>
                  <Text variant="caption" style={styles.filterLabel}>From Date:</Text>
                  <input
                    type="datetime-local"
                    value={fromDate}
                    onChange={(e) => setFromDate(e.target.value)}
                    style={{
                      padding: '8px',
                      borderRadius: '8px',
                      border: '1px solid #E0E0E0',
                      fontSize: '14px',
                      width: '100%',
                    }}
                  />
                </View>
                <View style={styles.dateField}>
                  <Text variant="caption" style={styles.filterLabel}>To Date:</Text>
                  <input
                    type="datetime-local"
                    value={toDate}
                    onChange={(e) => setToDate(e.target.value)}
                    style={{
                      padding: '8px',
                      borderRadius: '8px',
                      border: '1px solid #E0E0E0',
                      fontSize: '14px',
                      width: '100%',
                    }}
                  />
                </View>
              </View>

              {/* Filter Actions */}
              <View style={styles.filterActions}>
                <Button onPress={applyFilters} style={[styles.applyButton, { backgroundColor: accentColor }]}>
                  Apply Filters
                </Button>
                <Button variant="outline" onPress={clearFilters} style={styles.clearButton}>
                  Clear Filters
                </Button>
              </View>
            </>
          )}
        </Card>

        {/* Orders List */}
        <View style={styles.ordersSection}>
          <Text variant="h3" bold style={styles.sectionTitle}>
            📦 Orders ({orders.length})
          </Text>

          {orders.length === 0 ? (
            <Card style={styles.emptyCard}>
              <Text variant="h3" bold style={styles.emptyTitle}>
                No Orders Found
              </Text>
              <Text variant="body" color="secondary" style={styles.emptyText}>
                {statusFilter || fromDate || toDate
                  ? 'No orders match your filter criteria'
                  : 'No orders have been placed yet'}
              </Text>
            </Card>
          ) : (
            orders.map((order) => (
              <Card key={order.id} style={styles.orderCard}>
                {/* Order Header */}
                <View style={styles.orderHeader}>
                  <View style={styles.orderHeaderLeft}>
                    <Text style={styles.orderIcon}>{getStatusIcon(order.status)}</Text>
                    <View style={styles.orderInfoContainer}>
                      <Text variant="body" bold>Order #{order.id.slice(-8)}</Text>
                      <Text variant="caption" color="secondary">{formatDate(order.createdAt)}</Text>


                      {/* Current Status - Moved below order info */}
                      <View style={styles.statusContainer}>
                        <Text variant="caption" style={styles.currentStatusLabel}>Current Status:</Text>
                        <View style={[styles.statusBadge, { backgroundColor: getStatusColor(order.status), opacity: 0.7 }]}>
                          <Text style={styles.statusText}>{order.status}</Text>
                        </View>
                      </View>
                    </View>
                  </View>

                  {/* Quick Action Buttons - Top right corner, only for editable orders */}
                  {!['DELIVERED', 'REJECTED', 'CANCELED'].includes(order.status) && (
                    <View style={styles.actionButtonsColumn}>
                      <View style={styles.actionButtonsRow}>
                        {/* Accept Button - Hide if current status is ACCEPTED */}
                        {order.status !== 'ACCEPTED' && (
                          <TouchableOpacity
                            style={[styles.actionButton, { backgroundColor: '#34C759' }]}
                            onPress={() => handleQuickStatusUpdate(order, 'ACCEPTED')}
                          >
                            <View style={styles.actionButtonCircle}>
                              <Text style={styles.actionButtonText}>A</Text>
                            </View>
                          </TouchableOpacity>
                        )}

                        {/* Delivered Button - Hide if current status is DELIVERED */}
                        {order.status !== 'DELIVERED' && (
                          <TouchableOpacity
                            style={[styles.actionButton, { backgroundColor: '#5856D6' }]}
                            onPress={() => handleQuickStatusUpdate(order, 'DELIVERED')}
                          >
                            <View style={styles.actionButtonCircle}>
                              <Text style={styles.actionButtonText}>D</Text>
                            </View>
                          </TouchableOpacity>
                        )}

                        {/* Reject Button - Hide if current status is REJECTED */}
                        {order.status !== 'REJECTED' && (
                          <TouchableOpacity
                            style={[styles.actionButton, { backgroundColor: '#FF3B30' }]}
                            onPress={() => handleQuickStatusUpdate(order, 'REJECTED')}
                          >
                            <View style={styles.actionButtonCircle}>
                              <Text style={styles.actionButtonText}>R</Text>
                            </View>
                          </TouchableOpacity>
                        )}
                      </View>
                      <Text variant="caption" style={styles.actionHintText}>
                        Tap A to Accept, D to Delivered & R to Reject
                      </Text>

                      {/* Print and Share Invoice Buttons */}
                      <View style={styles.invoiceButtonsRow}>
                        {/* Print Invoice Button */}
                        <TouchableOpacity
                          style={styles.invoiceButton}
                          onPress={() => handlePrintInvoice(order)}
                        >
                          <View style={[styles.actionButtonCircle, styles.invoiceButtonCircle]}>
                            <Text style={styles.invoiceButtonIcon}>🖨️</Text>
                          </View>
                        </TouchableOpacity>

                        {/* Share Invoice Button */}
                        <TouchableOpacity
                          style={styles.invoiceButton}
                          onPress={() => handleShareInvoice(order)}
                        >
                          <View style={[styles.actionButtonCircle, styles.invoiceButtonCircle]}>
                            <Text style={styles.invoiceButtonIcon}>📤</Text>
                          </View>
                        </TouchableOpacity>
                      </View>
                      <Text variant="caption" style={styles.invoiceHintText}>
                        Print / Share Invoice
                      </Text>
                    </View>
                  )}
                </View>

                {/* Customer Info */}
                <View style={styles.customerInfo}>
                  <Text variant="body" style={styles.customerLabel}>
                    Customer: {order.customerName || `+${order.mobileCountryCode} ${order.customerMobile}`}
                  </Text>
                  {order.customerName && (
                    <Text variant="caption" color="secondary">📱 +{order.mobileCountryCode} {order.customerMobile}</Text>
                  )}
                  {order.customerEmail && (
                    <Text variant="caption" color="secondary">✉️ {order.customerEmail}</Text>
                  )}
                  {order.deliveryAddress && (
                    <View style={styles.deliveryAddressContainer}>
                      <Text variant="caption" color="secondary">📍 {order.deliveryAddress}</Text>
                      {order.deliveryLocation && order.deliveryLocation.valid &&
                       (order.deliveryLocation.latitude !== 0 || order.deliveryLocation.longitude !== 0) && (
                        <TouchableOpacity
                          style={styles.mapButton}
                          onPress={() => handleOpenMap(order)}
                        >
                          <Text style={styles.mapButtonText}>🗺️ Open in Map</Text>
                        </TouchableOpacity>
                      )}
                    </View>
                  )}
                </View>

                {/* Order Items */}
                <View style={styles.itemsSection}>
                  <Text variant="body" bold style={styles.itemsTitle}>Items:</Text>
                  {order.items.map((item, index) => (
                    <OrderItemWithDetails
                      key={[order.id, item.catalogId, index].join('-')}
                      item={item}
                      index={index}
                      catalogCache={catalogCache}
                      setCatalogCache={setCatalogCache}
                      accentColor={accentColor}
                      tokens={tokens}
                    />
                  ))}
                </View>

                {/* Price Breakdown */}
                {(() => {
                  // Calculate items total using effective prices (after item-level discounts)
                  const itemsTotal = order.items.reduce((sum, item) => {
                    const effectivePrice = item.effectivePricePerUnit || item.pricePerUnit;
                    const lineTotal = item.lineTotal || (item.quantity * effectivePrice);
                    return sum + lineTotal;
                  }, 0);

                  // Check for item-level discounts
                  const hasItemDiscounts = order.items.some(item => (item.discount || 0) > 0);
                  const originalTotal = order.items.reduce((sum, item) => sum + (item.quantity * item.pricePerUnit), 0);
                  const itemDiscountTotal = hasItemDiscounts ? originalTotal - itemsTotal : 0;

                  const deliveryCharges = order.deliveryCharges || 0;
                  const orderDiscount = order.discount || 0;
                  const discountType = order.discountType || 'FLAT';
                  const orderDiscountAmt = orderDiscount > 0
                    ? (discountType === 'PERCENTAGE' ? Math.min((itemsTotal * orderDiscount) / 100, itemsTotal) : Math.min(orderDiscount, itemsTotal))
                    : 0;

                  const hasBreakdown = deliveryCharges > 0 || itemDiscountTotal > 0 || orderDiscountAmt > 0;
                  if (!hasBreakdown) return null;

                  return (
                    <View style={styles.priceBreakdown}>
                      <View style={styles.breakdownRow}>
                        <Text variant="body" color="secondary">Items Subtotal:</Text>
                        <Text variant="body" color="secondary">₹{itemsTotal.toFixed(2)}</Text>
                      </View>
                      {itemDiscountTotal > 0 && (
                        <View style={[styles.breakdownRow, styles.discountBreakdownRow]}>
                          <Text variant="body" style={styles.discountLabel}>
                            🏷️ Item Discounts:
                          </Text>
                          <Text variant="body" style={styles.discountValue}>
                            - ₹{itemDiscountTotal.toFixed(2)}
                          </Text>
                        </View>
                      )}
                      {deliveryCharges > 0 && (
                        <View style={styles.breakdownRow}>
                          <Text variant="body" color="secondary">🚚 Delivery Charges:</Text>
                          <Text variant="body" color="secondary">+ ₹{deliveryCharges.toFixed(2)}</Text>
                        </View>
                      )}
                      {orderDiscountAmt > 0 && (
                        <View style={[styles.breakdownRow, styles.discountBreakdownRow]}>
                          <Text variant="body" style={styles.discountLabel}>
                            🏷️ Order Discount{discountType === 'PERCENTAGE' ? ` (${orderDiscount}%)` : ''}:
                          </Text>
                          <Text variant="body" style={styles.discountValue}>
                            - ₹{orderDiscountAmt.toFixed(2)}
                          </Text>
                        </View>
                      )}
                    </View>
                  );
                })()}

                {/* Total / Final Amount */}
                {(() => {
                  // Check if any discount exists (item-level or order-level)
                  const hasItemDiscounts = order.items.some(item => (item.discount || 0) > 0);
                  const hasOrderDiscount = (order.discount || 0) > 0;
                  const hasAnyDiscount = hasItemDiscounts || hasOrderDiscount;

                  return (
                    <View style={[styles.totalRow, hasAnyDiscount && styles.finalAmountRow]}>
                      <View>
                        <Text variant="body" bold style={hasAnyDiscount ? styles.finalAmountLabel : undefined}>
                          {hasAnyDiscount ? '🏷️ Final Amount' : 'Total Amount'}
                        </Text>
                        {hasAnyDiscount && (
                          <Text variant="caption" color="secondary" style={styles.afterDiscountHint}>after discount</Text>
                        )}
                      </View>
                      <Text variant="h3" bold style={[{ color: accentColor }, hasAnyDiscount && styles.finalAmountValue]}>
                        ₹{order.totalAmount.toFixed(2)}
                      </Text>
                    </View>
                  );
                })()}

                {/* Order Source */}
                <Text variant="caption" color="secondary" style={styles.sourceText}>
                  Source: {order.orderSource}
                </Text>

                {/* Print / Share Invoice - visible for ALL orders */}
                {['DELIVERED', 'REJECTED', 'CANCELED'].includes(order.status) && (
                  <View style={styles.completedInvoiceRow}>
                    <TouchableOpacity
                      style={[styles.completedInvoiceButton, { borderColor: accentColor }]}
                      onPress={() => handlePrintInvoice(order)}
                    >
                      <Text style={[styles.completedInvoiceButtonText, { color: accentColor }]}>🖨️ Print Invoice</Text>
                    </TouchableOpacity>
                    <TouchableOpacity
                      style={[styles.completedInvoiceButton, { borderColor: accentColor }]}
                      onPress={() => handleShareInvoice(order)}
                    >
                      <Text style={[styles.completedInvoiceButtonText, { color: accentColor }]}>📤 Share Invoice</Text>
                    </TouchableOpacity>
                  </View>
                )}

                {/* Edit/View Button - View only for completed/rejected/canceled orders */}
                {['DELIVERED', 'REJECTED', 'CANCELED'].includes(order.status) ? (
                  <View style={styles.viewOnlyBadge}>
                    <Text style={styles.viewOnlyText}>👁️ View Only - Order {order.status}</Text>
                  </View>
                ) : (
                  <Button onPress={() => handleEditOrder(order)} style={[styles.editButton, { backgroundColor: accentColor }]}>
                    ✏️ Edit Order
                  </Button>
                )}
              </Card>
            ))
          )}
        </View>
      </ScrollView>

      {/* Edit Order Modal */}
      <Modal
        visible={showEditModal}
        animationType="slide"
        transparent={false}
        onRequestClose={() => setShowEditModal(false)}
      >
        <View style={[styles.modalContainer, { backgroundColor: themeColors.secondary }]}>
          <ScrollView contentContainerStyle={styles.modalContent}>
            <Card style={styles.editCard}>
              <Text variant="h2" bold style={styles.modalTitle}>
                ✏️ Edit Order
              </Text>

              {editingOrder && (
                <>
                  {/* Order ID */}
                  <Text variant="body" color="secondary" style={styles.orderIdText}>
                    Order ID: {editingOrder.id}
                  </Text>

                  {/* Customer Info */}
                  <Text variant="body" color="secondary" style={styles.orderIdText}>
                    Customer: {editingOrder.customerName || `+${editingOrder.mobileCountryCode} ${editingOrder.customerMobile}`}
                  </Text>

                  {/* Status Selector */}
                  <View style={styles.formField}>
                    <Text variant="body" bold style={styles.fieldLabel}>Order Status *</Text>
                    <View style={styles.statusSelector}>
                      {['CREATED', 'ACCEPTED', 'DELIVERED', 'REJECTED', 'CANCELED'].map((status) => (
                        <TouchableOpacity
                          key={status}
                          style={[
                            styles.statusSelectorButton,
                            editForm.status === status && { backgroundColor: accentColor },
                          ]}
                          onPress={() => setEditForm({ ...editForm, status })}
                        >
                          <Text
                            style={[
                              styles.statusSelectorText,
                              editForm.status === status && styles.statusSelectorTextActive,
                            ]}
                          >
                            {status}
                          </Text>
                        </TouchableOpacity>
                      ))}
                    </View>
                  </View>

                  {/* Order Items */}
                  <Text variant="body" bold style={styles.fieldLabel}>Order Items</Text>
                  {editForm.items.map((item, index) => (
                    <EditOrderItemWithDetails
                      key={['edit', item.catalogId, index].join('-')}
                      item={item}
                      index={index}
                      catalogCache={catalogCache}
                      setCatalogCache={setCatalogCache}
                      accentColor={accentColor}
                      tokens={tokens}
                      handleUpdateItem={handleUpdateItem}
                    />
                  ))}

                  {/* Delivery Charges Input */}
                  <View style={styles.formField}>
                    <Text variant="body" bold style={styles.fieldLabel}>Delivery Charges (₹)</Text>
                    <Input
                      placeholder="Enter delivery charges (non-negative)"
                      value={(editForm.deliveryCharges || 0).toString()}
                      onChangeText={handleUpdateDeliveryCharges}
                      keyboardType="decimal-pad"
                      placeholderTextColor={colors.gray5}
                    />
                  </View>

                  {/* Discount Section */}
                  <View style={styles.formField}>
                    <Text variant="body" bold style={styles.fieldLabel}>🏷️ Discount</Text>
                    {/* Discount Type Toggle */}
                    <View style={styles.discountTypeRow}>
                      <TouchableOpacity
                        style={[
                          styles.discountTypeButton,
                          (editForm.discountType || 'FLAT') === 'FLAT' && { backgroundColor: accentColor },
                        ]}
                        onPress={() => handleUpdateDiscountType('FLAT')}
                      >
                        <Text style={[
                          styles.discountTypeText,
                          (editForm.discountType || 'FLAT') === 'FLAT' && styles.discountTypeTextActive,
                        ]}>
                          ₹ Flat Amount
                        </Text>
                      </TouchableOpacity>
                      <TouchableOpacity
                        style={[
                          styles.discountTypeButton,
                          editForm.discountType === 'PERCENTAGE' && { backgroundColor: accentColor },
                        ]}
                        onPress={() => handleUpdateDiscountType('PERCENTAGE')}
                      >
                        <Text style={[
                          styles.discountTypeText,
                          editForm.discountType === 'PERCENTAGE' && styles.discountTypeTextActive,
                        ]}>
                          % Percentage
                        </Text>
                      </TouchableOpacity>
                    </View>
                    <Input
                      placeholder={
                        (editForm.discountType || 'FLAT') === 'PERCENTAGE'
                          ? 'Enter discount % (0–100)'
                          : 'Enter discount amount in ₹'
                      }
                      value={(editForm.discount || 0) > 0 ? (editForm.discount || 0).toString() : ''}
                      onChangeText={handleUpdateDiscount}
                      keyboardType="decimal-pad"
                      placeholderTextColor={colors.gray5}
                    />
                    {(editForm.discount || 0) > 0 && (() => {
                      const itemsTotal = editForm.items.reduce((sum, item) => {
                        const effectivePrice = item.effectivePricePerUnit || item.pricePerUnit;
                        const lineTotal = item.lineTotal || (item.quantity * effectivePrice);
                        return sum + lineTotal;
                      }, 0);
                      const discountType = editForm.discountType || 'FLAT';
                      const discountAmt = discountType === 'PERCENTAGE'
                        ? Math.min((itemsTotal * (editForm.discount || 0)) / 100, itemsTotal)
                        : Math.min(editForm.discount || 0, itemsTotal);
                      return (
                        <Text variant="caption" style={styles.discountHint}>
                          Discount applied: - ₹{discountAmt.toFixed(2)}
                        </Text>
                      );
                    })()}
                  </View>

                  {/* Price Breakdown */}
                  <View style={styles.priceBreakdownEdit}>
                    {/* Calculate all values first */}
                    {(() => {
                      // Original total (before item discounts)
                      const originalTotal = editForm.items.reduce((sum, item) => sum + (item.quantity * item.pricePerUnit), 0);

                      // Items subtotal (after item discounts)
                      const itemsSubtotal = editForm.items.reduce((sum, item) => {
                        const effectivePrice = item.effectivePricePerUnit || item.pricePerUnit;
                        const lineTotal = item.lineTotal || (item.quantity * effectivePrice);
                        return sum + lineTotal;
                      }, 0);

                      // Item-level discount total
                      const itemDiscountTotal = originalTotal - itemsSubtotal;

                      // Order-level discount
                      const orderDiscount = editForm.discount || 0;
                      const discountType = editForm.discountType || 'FLAT';
                      const orderDiscountAmt = orderDiscount > 0
                        ? (discountType === 'PERCENTAGE'
                            ? Math.min((itemsSubtotal * orderDiscount) / 100, itemsSubtotal)
                            : Math.min(orderDiscount, itemsSubtotal))
                        : 0;

                      return (
                        <>
                          <View style={styles.breakdownRow}>
                            <Text variant="body" color="secondary">Items Subtotal:</Text>
                            <Text variant="body" color="secondary">
                              ₹{itemsSubtotal.toFixed(2)}
                            </Text>
                          </View>
                          {itemDiscountTotal > 0 && (
                            <View style={styles.breakdownRow}>
                              <Text variant="body" style={{ color: '#34C759' }}>Item Discounts:</Text>
                              <Text variant="body" style={{ color: '#34C759', fontWeight: '700' }}>
                                - ₹{itemDiscountTotal.toFixed(2)}
                              </Text>
                            </View>
                          )}
                          {(editForm.deliveryCharges || 0) > 0 && (
                            <View style={styles.breakdownRow}>
                              <Text variant="body" color="secondary">Delivery Charges:</Text>
                              <Text variant="body" color="secondary">₹{(editForm.deliveryCharges || 0).toFixed(2)}</Text>
                            </View>
                          )}
                          {orderDiscountAmt > 0 && (
                            <View style={styles.breakdownRow}>
                              <Text variant="body" style={{ color: '#34C759' }}>
                                Order Discount{discountType === 'PERCENTAGE' ? ` (${orderDiscount}%)` : ''}:
                              </Text>
                              <Text variant="body" style={{ color: '#34C759', fontWeight: '700' }}>
                                - ₹{orderDiscountAmt.toFixed(2)}
                              </Text>
                            </View>
                          )}
                        </>
                      );
                    })()}
                  </View>

                  {/* Total / Final Amount */}
                  <View style={styles.totalEditRow}>
                    <Text variant="h3" bold>
                      {(editForm.discount || 0) > 0 ? '🏷️ Final Amount:' : 'Total Amount:'}
                    </Text>
                    <Text variant="h2" bold style={{ color: accentColor }}>
                      ₹{editForm.totalAmount.toFixed(2)}
                    </Text>
                  </View>

                  {/* Action Buttons */}
                  <View style={styles.modalActions}>
                    <Button
                      variant="outline"
                      onPress={() => setShowEditModal(false)}
                      style={styles.cancelButton}
                      disabled={saving}
                    >
                      Cancel
                    </Button>
                    <Button
                      onPress={handleSaveOrder}
                      style={[styles.saveButton, { backgroundColor: accentColor }]}
                      disabled={saving}
                    >
                      {saving ? 'Saving...' : 'Save Changes'}
                    </Button>
                  </View>
                </>
              )}
            </Card>
          </ScrollView>
        </View>
      </Modal>
    </>
  );
};

const styles = StyleSheet.create({
  container: {
    flexGrow: 1,
    paddingVertical: 16,
    paddingHorizontal: 12,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  loadingText: {
    marginTop: 16,
  },
  filtersCard: {
    marginBottom: 16,
    padding: 16,
  },
  filterHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 0,
  },
  toggleIcon: {
    fontSize: 18,
    color: colors.gray6,
    fontWeight: 'bold',
  },
  sectionTitle: {
    marginBottom: 0,
  },
  filterRow: {
    marginBottom: 12,
    marginTop: 16,
  },
  filterLabel: {
    marginBottom: 8,
    fontWeight: '600',
  },
  statusButtons: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
  },
  statusButton: {
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 16,
    backgroundColor: colors.gray2,
  },
  statusButtonText: {
    fontSize: 12,
    color: colors.gray7,
  },
  statusButtonTextActive: {
    color: colors.white,
    fontWeight: 'bold',
  },
  dateFilterRow: {
    flexDirection: 'row',
    gap: 12,
    marginBottom: 12,
  },
  dateField: {
    flex: 1,
  },
  filterActions: {
    flexDirection: 'row',
    gap: 12,
    marginTop: 8,
  },
  applyButton: {
    flex: 1,
  },
  clearButton: {
    flex: 1,
  },
  ordersSection: {
    marginBottom: 20,
  },
  emptyCard: {
    padding: 40,
    alignItems: 'center',
  },
  emptyTitle: {
    marginBottom: 8,
  },
  emptyText: {
    textAlign: 'center',
  },
  orderCard: {
    marginBottom: 12,
    padding: 16,
  },
  orderHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 12,
  },
  orderHeaderLeft: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 8,
    flex: 1,
  },
  orderIcon: {
    fontSize: 24,
  },
  orderInfoContainer: {
    flex: 1,
  },
  statusContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginTop: 6,
  },
  currentStatusLabel: {
    fontSize: 10,
    color: colors.gray6,
    fontWeight: '600',
  },
  statusBadge: {
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 12,
    minWidth: 80,
    alignItems: 'center',
  },
  statusText: {
    color: colors.white,
    fontSize: 11,
    fontWeight: 'bold',
  },
  actionButtonsColumn: {
    alignItems: 'center',
  },
  actionButtonsRow: {
    flexDirection: 'row',
    gap: 8,
    alignItems: 'center',
  },
  actionButton: {
    alignItems: 'center',
    justifyContent: 'center',
    padding: 4,
  },
  actionButtonCircle: {
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: 'rgba(255, 255, 255, 0.9)',
    justifyContent: 'center',
    alignItems: 'center',
    borderWidth: 2,
    borderColor: 'rgba(0, 0, 0, 0.1)',
  },
  actionButtonText: {
    color: colors.gray8,
    fontSize: 14,
    fontWeight: 'bold',
  },
  actionHintText: {
    fontSize: 9,
    color: colors.gray6,
    marginTop: 4,
    textAlign: 'center',
    maxWidth: 120,
  },
  invoiceButtonsRow: {
    flexDirection: 'row',
    gap: 8,
    alignItems: 'center',
    marginTop: 8,
  },
  invoiceButton: {
    alignItems: 'center',
    justifyContent: 'center',
    padding: 4,
  },
  invoiceButtonCircle: {
    backgroundColor: 'rgba(245, 245, 245, 0.95)',
    borderColor: colors.gray4,
  },
  invoiceButtonIcon: {
    fontSize: 16,
  },
  invoiceHintText: {
    fontSize: 9,
    color: colors.gray6,
    marginTop: 4,
    textAlign: 'center',
    maxWidth: 120,
  },
  customerInfo: {
    marginBottom: 12,
    paddingBottom: 12,
    borderBottomWidth: 1,
    borderBottomColor: colors.gray3,
  },
  customerLabel: {
    fontWeight: '600',
    marginBottom: 4,
  },
  deliveryAddressContainer: {
    marginTop: 8,
    paddingTop: 8,
    borderTopWidth: 1,
    borderTopColor: colors.gray3,
  },
  mapButton: {
    marginTop: 8,
    paddingVertical: 8,
    paddingHorizontal: 12,
    backgroundColor: colors.primary,
    borderRadius: 6,
    alignItems: 'center',
  },
  mapButtonText: {
    color: colors.white,
    fontSize: 13,
    fontWeight: '600',
  },
  itemsSection: {
    marginBottom: 12,
  },
  itemsTitle: {
    marginBottom: 8,
  },
  itemRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingVertical: 4,
    alignItems: 'center',
  },
  loadingItemText: {
    marginLeft: 8,
  },
  itemDetailRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingVertical: 8,
    paddingHorizontal: 12,
    marginVertical: 4,
    backgroundColor: colors.gray1,
    borderRadius: 8,
    borderLeftWidth: 3,
    borderLeftColor: colors.primary,
  },
  itemInfo: {
    flex: 1,
  },
  itemNamePriceRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 6,
  },
  itemName: {
    fontSize: 14,
    flex: 1,
    marginRight: 12,
  },
  itemPriceInfo: {
    alignItems: 'flex-end',
    gap: 2,
  },
  itemQuantityText: {
    fontSize: 11,
  },
  itemTotalPrice: {
    fontSize: 15,
    fontWeight: 'bold',
  },
  itemDescription: {
    fontSize: 12,
    marginBottom: 4,
    lineHeight: 16,
  },
  itemCategory: {
    fontSize: 11,
    color: colors.gray6,
    marginBottom: 4,
  },
  itemCategoryInline: {
    fontSize: 12,
    fontWeight: 'normal',
    color: colors.gray6,
  },
  itemQuantityRow: {
    marginTop: 4,
  },
  itemPriceContainer: {
    justifyContent: 'center',
    alignItems: 'flex-end',
  },
  itemPrice: {
    fontSize: 15,
    color: colors.primary,
  },
  totalRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingTop: 12,
    borderTopWidth: 1,
    borderTopColor: colors.gray3,
    marginBottom: 8,
  },
  priceBreakdown: {
    marginTop: 12,
    marginBottom: 8,
    paddingTop: 8,
    borderTopWidth: 1,
    borderTopColor: colors.gray2,
  },
  priceBreakdownEdit: {
    marginTop: 12,
    marginBottom: 4,
  },
  breakdownRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 6,
  },
  sourceText: {
    marginBottom: 12,
  },
  editButton: {
    marginTop: 8,
  },
  viewOnlyBadge: {
    marginTop: 8,
    paddingVertical: 12,
    paddingHorizontal: 16,
    backgroundColor: colors.gray2,
    borderRadius: 8,
    alignItems: 'center',
  },
  viewOnlyText: {
    color: colors.gray6,
    fontSize: 14,
    fontWeight: '600',
  },
  modalContainer: {
    flex: 1,
  },
  modalContent: {
    padding: 16,
  },
  editCard: {
    padding: 20,
  },
  modalTitle: {
    marginBottom: 8,
  },
  orderIdText: {
    marginBottom: 20,
  },
  formField: {
    marginBottom: 20,
  },
  fieldLabel: {
    marginBottom: 8,
  },
  statusSelector: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
  },
  statusSelectorButton: {
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 16,
    backgroundColor: colors.gray2,
  },
  statusSelectorText: {
    fontSize: 12,
    color: colors.gray7,
  },
  statusSelectorTextActive: {
    color: colors.white,
    fontWeight: 'bold',
  },
  itemEditRow: {
    marginBottom: 16,
    padding: 12,
    backgroundColor: colors.gray1,
    borderRadius: 8,
    borderLeftWidth: 3,
    borderLeftColor: colors.primary,
  },
  editItemHeader: {
    marginBottom: 12,
    paddingBottom: 12,
    borderBottomWidth: 1,
    borderBottomColor: colors.gray3,
  },
  editItemLoadingRow: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  editItemNamePriceRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 6,
  },
  editItemName: {
    fontSize: 15,
    flex: 1,
    marginRight: 12,
    color: colors.gray8,
  },
  editItemTotal: {
    fontSize: 16,
    fontWeight: 'bold',
  },
  editItemDescription: {
    fontSize: 12,
    marginBottom: 4,
    lineHeight: 16,
  },
  editItemCategory: {
    fontSize: 11,
    color: colors.gray6,
  },
  editItemCategoryInline: {
    fontSize: 13,
    fontWeight: 'normal',
    color: colors.gray6,
  },
  itemUnit: {
    fontWeight: '600',
    marginBottom: 8,
    fontSize: 12,
  },
  itemInputs: {
    flexDirection: 'row',
    gap: 12,
  },
  itemInputsCompact: {
    flexDirection: 'row',
    gap: 12,
  },
  itemInputField: {
    flex: 1,
  },
  itemInputFieldCompact: {
    flex: 1,
  },
  inputLabelCompact: {
    fontSize: 11,
    fontWeight: '600',
    marginBottom: 4,
    color: colors.gray7,
  },
  itemInput: {
    fontSize: 12,
  },
  itemInputCompact: {
    fontSize: 13,
    paddingVertical: 8,
  },
  totalEditRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingTop: 16,
    borderTopWidth: 2,
    borderTopColor: colors.gray3,
    marginBottom: 20,
  },
  modalActions: {
    flexDirection: 'row',
    gap: 12,
  },
  cancelButton: {
    flex: 1,
  },
  saveButton: {
    flex: 1,
  },
  discountTypeRow: {
    flexDirection: 'row',
    gap: 8,
    marginBottom: 10,
  },
  discountTypeButton: {
    flex: 1,
    paddingVertical: 8,
    paddingHorizontal: 12,
    borderRadius: 12,
    backgroundColor: colors.gray2,
    alignItems: 'center',
  },
  discountTypeText: {
    fontSize: 12,
    fontWeight: '600',
    color: colors.gray7,
  },
  discountTypeTextActive: {
    color: colors.white,
    fontWeight: 'bold',
  },
  discountHint: {
    marginTop: 6,
    color: '#34C759',
    fontWeight: '600',
  },
  completedInvoiceRow: {
    flexDirection: 'row',
    gap: 10,
    marginBottom: 8,
    marginTop: 4,
  },
  completedInvoiceButton: {
    flex: 1,
    paddingVertical: 10,
    paddingHorizontal: 12,
    borderRadius: 8,
    borderWidth: 1,
    alignItems: 'center',
  },
  completedInvoiceButtonText: {
    fontSize: 13,
    fontWeight: '600',
  },
  discountBreakdownRow: {
    backgroundColor: '#F0FFF4',
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 6,
    marginTop: 2,
  },
  discountLabel: {
    color: '#1F7A3F',
    fontWeight: '600',
  },
  discountValue: {
    color: '#1F7A3F',
    fontWeight: '700',
  },
  finalAmountRow: {
    backgroundColor: '#F0F8FF',
    paddingHorizontal: 10,
    paddingVertical: 10,
    borderRadius: 8,
    borderTopWidth: 2,
    marginTop: 4,
  },
  finalAmountLabel: {
    fontSize: 16,
    fontWeight: '700',
  },
  finalAmountValue: {
    fontSize: 22,
  },
  afterDiscountHint: {
    fontSize: 10,
    marginTop: 1,
  },
});
